import React, { useCallback, useState } from 'react';

import {
    autoUpdate,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    size,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as LoadingIcon } from '@pkg/assets/icons/v2/loading-bars.svg';
import { FieldSearch, type FieldSearchProps } from '@pkg/components';
import { useGetGlobalSearchResults } from '@pkg/components/GlobalSearch/hooks/useGetGlobalSearchResults';
import { tokens as fieldTextTokens } from '@pkg/components/forms/FieldText/FieldText.tokens';
import { type KeyPressCallback, useInstantRef, useKeyPress } from '@pkg/hooks';

import { EmojiPickerComboFloating } from '../EmojiPickerComboFloating';
import { GeneralSearchResult } from '../SearchResult';

import { EmojiPickerWrapper, SearchLoadingWrapper, SearchResultWrapper } from './styles';

export function SearchInput() {
    const [translate] = useTranslation(['global_search']);
    const navigate = useNavigate();

    const [inputElement, inputRef] = useInstantRef<HTMLInputElement>();

    const [value, setValue] = useState('');
    const [isOpen, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>();
    const [focusedLink, setFocusedLink] = useState('');

    const { x, y, strategy, refs, context } = useFloating({
        placement: 'bottom',
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: (value) => {
            if (!value) {
                setFocusedLink('');
                setActiveIndex(undefined);
            }
            setOpen(value);
        },
        middleware: [
            offset(6),
            size({
                apply({ rects, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                        maxHeight: `${availableHeight}px`,
                    });
                },
                padding: 10,
            }),
        ],
    });

    const { data, isLoading, totalResults } = useGetGlobalSearchResults(value);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'listbox' });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

    function handleSelectEmoji(selectedEmoji: string) {
        setOpen(true);
        setValue((currentValue) => currentValue + selectedEmoji);
    }

    function handleCloseResult() {
        setOpen(false);
        setActiveIndex(undefined);
        setFocusedLink('');
    }

    const handleChangeInput: FieldSearchProps['onChange'] = (newValue = '') => {
        const stringified = newValue.toString();
        setValue(stringified);

        if (stringified) {
            setOpen(true);
        } else {
            handleCloseResult();
        }
    };

    const handleDown: KeyPressCallback = useCallback(() => {
        setActiveIndex((prevIndex) => {
            if (prevIndex === undefined) return 0;
            else if (prevIndex < totalResults - 1) return prevIndex + 1;
            else return 0;
        });
    }, [totalResults]);

    const handleUp: KeyPressCallback = useCallback(() => {
        setActiveIndex((prevIndex) => {
            if (prevIndex === undefined) return 0;
            else if (prevIndex > 0) return prevIndex - 1;
            else return totalResults - 1;
        });
    }, [totalResults]);

    const handleEnter: KeyPressCallback = useCallback(() => {
        if (activeIndex !== undefined && focusedLink) {
            handleCloseResult();
            navigate(focusedLink);
        }
    }, [activeIndex, focusedLink, navigate]);

    useKeyPress(['ArrowDown'], handleDown, {
        target: inputElement,
    });

    useKeyPress(['ArrowUp'], handleUp, {
        target: inputElement,
    });

    useKeyPress(['Enter'], handleEnter, {
        target: inputElement,
    });

    function handleFocusInput() {
        if (value && !isOpen) {
            setOpen(true);
        }
    }

    const emojiPickerSize = fieldTextTokens.height.small - fieldTextTokens.gap.small * 2;
    const emojiPickerMarkup = (
        <EmojiPickerWrapper>
            <EmojiPickerComboFloating
                onSelectEmoji={handleSelectEmoji}
                autoPlacementOptions={{ allowedPlacements: ['right-start', 'top-end', 'bottom-end'] }}
            />
        </EmojiPickerWrapper>
    );

    return (
        <>
            <FieldSearch
                backdropBlur
                wrapperRef={refs.setReference}
                inputRef={inputRef}
                value={value}
                placeholder={translate('search_placeholder')}
                suffix={emojiPickerMarkup}
                suffixOffset={emojiPickerSize}
                onFocus={handleFocusInput}
                onChange={handleChangeInput}
                popoverProps={{
                    ...getReferenceProps({
                        'aria-autocomplete': 'list',
                    }),
                }}
            />

            <FloatingPortal>
                {isOpen && (
                    <FloatingFocusManager context={context} visuallyHiddenDismiss initialFocus={-1}>
                        <SearchResultWrapper
                            {...getFloatingProps({
                                ref: refs.setFloating,
                                style: {
                                    position: strategy,
                                    left: x ?? 0,
                                    top: y ?? 0,
                                },
                            })}
                            ref={refs.setFloating}
                        >
                            {isLoading && (
                                <SearchLoadingWrapper aria-label="Loading">
                                    <LoadingIcon aria-hidden="true" />
                                </SearchLoadingWrapper>
                            )}

                            <GeneralSearchResult
                                results={data}
                                onResultClicked={handleCloseResult}
                                loading={isLoading}
                                activeIndex={activeIndex}
                                onFocusedElement={setFocusedLink}
                            />
                        </SearchResultWrapper>
                    </FloatingFocusManager>
                )}
            </FloatingPortal>
        </>
    );
}
